import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import CapabilityNode from './CapabilityNode';
import CapabilitySetNode from './CapabilitySetNode';
import { useStandardCollator } from 'js/utils/string';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { autoScrollWindowForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { extractInstruction } from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';

import { TreeContext } from '../TreeContext';
import {
  alphabeticalPositions,
  buildCapabilityLookup,
  resolveCapabilityMove,
  sortCapabilities,
} from '../ordering';

function CapabilityTree({
  capabilitySet,
  capabilities,
  onAddCapability,
  onEditCapability,
  onMoveCapability,
  onRepositionCapabilities,
}) {
  const path = [];
  const standardCollator = useStandardCollator();
  const context = useContext(TreeContext);
  const sortedCapabilities = useMemo(
    () => sortCapabilities(capabilities, standardCollator),
    [capabilities, standardCollator]
  );
  const lookup = useMemo(
    () => buildCapabilityLookup(capabilities, standardCollator),
    [capabilities, standardCollator]
  );

  const handleSortChildren = useCallback(
    (children) =>
      onRepositionCapabilities(
        alphabeticalPositions(children, standardCollator)
      ),
    [onRepositionCapabilities, standardCollator]
  );

  useEffect(() => {
    return autoScrollWindowForElements();
  }, []);

  useEffect(() => {
    return combine(
      monitorForElements({
        canMonitor: ({ source }) =>
          source.data.uniqueContextId === context.uniqueContextId,
        onDrop: (args) => {
          const { location, source } = args;

          if (
            !location.current.dropTargets.length ||
            source.data.type !== 'capability'
          ) {
            return;
          }

          const dropTarget = location.current.dropTargets[0];
          const { id, parent_id } = source.data.capability;

          const move = resolveCapabilityMove({
            lookup,
            rootCapabilities: capabilities,
            collator: standardCollator,
            sourceId: id,
            targetId:
              dropTarget.data.type === 'capability'
                ? dropTarget.data.capability.id
                : null,
            instruction: extractInstruction(dropTarget.data),
          });

          if (move) {
            onMoveCapability({
              id,
              old_parent_id: parent_id ?? null,
              new_parent_id: move.parentId,
              positions: move.positions,
            });
          }
        },
      })
    );
  }, [context, onMoveCapability, lookup, capabilities, standardCollator]);

  return (
    <div style={{ maxWidth: '600px' }}>
      <CapabilitySetNode
        key={capabilitySet.id}
        capabilitySet={capabilitySet}
        onAddChild={onAddCapability}
        onEdit={onEditCapability}
        onSortChildren={() => handleSortChildren(capabilities)}
        canSortChildren={sortedCapabilities.length > 1}
      />
      {sortedCapabilities.map((capability, index) => (
        <CapabilityNode
          key={capability.id}
          path={path}
          capability={capability}
          isLastInGroup={index === sortedCapabilities.length - 1}
          onAddChild={onAddCapability}
          onEdit={onEditCapability}
          onSortChildren={handleSortChildren}
        />
      ))}
    </div>
  );
}

export default CapabilityTree;
